import {
  Paper,
  Icon,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import useJobsTableData from "hooks/jobs/useJobsTableData";
import { JobStatus } from "generated/graphql";
import JobCard from "./JobCard";
import FiltersDrawer, { SortOption } from "./FiltersDrawer";

const sortOptions: Record<SortOption, { sortKey: string; sortDirection: string }> = {
  WON_DATE_DESC: { sortKey: "WON_DATE", sortDirection: "desc" },
  WON_DATE_ASC: { sortKey: "WON_DATE", sortDirection: "asc" },
  CITY_ASC: { sortKey: "CITY", sortDirection: "asc" },
  CITY_DESC: { sortKey: "CITY", sortDirection: "desc" },
  TOTAL_ASC: { sortKey: "JOB_TOTAL", sortDirection: "asc" },
  TOTAL_DESC: { sortKey: "JOB_TOTAL", sortDirection: "desc" },
};

interface JobsListSidebarProps {
  jobsTableData: ReturnType<typeof useJobsTableData>;
  selectedCities: string[];
  onCitiesChange: (cities: string[]) => void;
  view: "calendar" | "map";
  onViewChange: (view: "calendar" | "map") => void;
}

export default function JobsListSidebar({
  jobsTableData,
  selectedCities,
  onCitiesChange,
  view,
  onViewChange,
}: JobsListSidebarProps) {
  const {
    data: jobs,
    loading,
    error,
    filtering,
    organizationProductTypeIds,
    setOrganizationProductTypeIds,
    pagination,
    sorting,
  } = jobsTableData;

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>(() => {
    const savedFilters = JSON.parse(localStorage.getItem("JobsSchedulesFilters") || "{}");
    return savedFilters.sortBy || "WON_DATE_DESC";
  });

  const saveFiltersToLocalStorage = useCallback(
    (updates: {
      statuses?: JobStatus[];
      searchTerm?: string;
      productTypes?: any[];
      cities?: string[];
      sortBy?: SortOption;
    }) => {
      const currentFilters = JSON.parse(localStorage.getItem("JobsSchedulesFilters") || "{}");
      const filtersToSave = {
        searchTerm: updates.searchTerm ?? currentFilters.searchTerm ?? filtering.searchTerm,
        statuses: updates.statuses ?? currentFilters.statuses ?? filtering.statuses,
        organizationProductTypeIds:
          updates.productTypes ??
          currentFilters.organizationProductTypeIds ??
          organizationProductTypeIds,
        cities: updates.cities ?? currentFilters.cities ?? selectedCities,
        sortBy: updates.sortBy ?? currentFilters.sortBy ?? sortBy,
      };
      localStorage.setItem("JobsSchedulesFilters", JSON.stringify(filtersToSave));
    },
    [filtering.searchTerm, filtering.statuses, organizationProductTypeIds, selectedCities, sortBy]
  );

  useEffect(() => {
    const { sortKey, sortDirection } = sortOptions[sortBy];
    sorting.setSort({ sortKey, sortDirection });
  }, [sortBy]);

  const handleStatusChange = useCallback(
    (statuses: JobStatus[]) => {
      filtering.setStatuses(statuses);
      saveFiltersToLocalStorage({ statuses });
    },
    [filtering, saveFiltersToLocalStorage]
  );

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      filtering.setSearchTerm(searchValue);
      saveFiltersToLocalStorage({ searchTerm: searchValue });
    },
    [filtering, saveFiltersToLocalStorage]
  );

  const handleProductTypeChange = useCallback(
    (selectedProductTypes: any[]) => {
      setOrganizationProductTypeIds(selectedProductTypes);
      saveFiltersToLocalStorage({ productTypes: selectedProductTypes });
    },
    [setOrganizationProductTypeIds, saveFiltersToLocalStorage]
  );

  const handleCitiesChange = useCallback(
    (cities: string[]) => {
      onCitiesChange(cities);
      saveFiltersToLocalStorage({ cities });
    },
    [onCitiesChange, saveFiltersToLocalStorage]
  );

  const handleSortChange = useCallback(
    (newSortBy: SortOption) => {
      setSortBy(newSortBy);
      saveFiltersToLocalStorage({ sortBy: newSortBy });
    },
    [saveFiltersToLocalStorage]
  );

  if (loading && (!jobs || jobs.length === 0)) {
    return (
      <Paper
        elevation={0}
        sx={{
          height: "100vh",
          borderRadius: 0,
          borderRight: "1px solid",
          borderColor: "grey.200",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100vh",
        borderRadius: 0,
        borderRight: "1px solid",
        borderColor: "grey.200",
        backgroundColor: "#f8f9fa",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MDBox flex={1} sx={{ overflowY: "auto", minHeight: 0, position: "relative" }}>
        {loading && jobs && jobs.length > 0 && (
          <MDBox
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <CircularProgress size={24} />
          </MDBox>
        )}

        <MDBox pt={3} pl={2} pr={2}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <MDTypography variant="h6" fontWeight="bold">
              JOBS SCHEDULE
            </MDTypography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(_, nextView: "calendar" | "map" | null) => {
                if (nextView) {
                  onViewChange(nextView);
                }
              }}
              sx={{ ml: "auto" }}
            >
              <ToggleButton value="calendar" aria-label="List view">
                <Icon fontSize="small">view_list</Icon>
              </ToggleButton>
              <ToggleButton value="map" aria-label="Map view">
                <Icon fontSize="small">map</Icon>
              </ToggleButton>
            </ToggleButtonGroup>
          </MDBox>

          <MDBox display="flex" gap={1} mb={2}>
            <MDBox position="relative" flex={1}>
              <Icon
                sx={{
                  position: "absolute",
                  left: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "grey.500",
                  fontSize: "20px",
                }}
              >
                search
              </Icon>
              <MDBox
                component="input"
                placeholder="Search"
                value={filtering.searchTerm || ""}
                onChange={(e) => handleSearchChange((e.target as HTMLInputElement).value)}
                sx={{
                  width: "100%",
                  height: "48px",
                  padding: "16px 16px 16px 52px",
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: "8px",
                  fontSize: "16px",
                  backgroundColor: "white!important",
                  "&:focus": {
                    outline: "none",
                    borderColor: "primary.main",
                  },
                  "&::placeholder": {
                    color: "grey.500",
                  },
                }}
              />
            </MDBox>
            <IconButton
              onClick={() => setFiltersOpen(!filtersOpen)}
              sx={{
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: "8px",
                width: "48px",
                height: "48px",
                backgroundColor: filtersOpen ? "grey.200" : "white",
                "&:hover": {
                  backgroundColor: filtersOpen ? "grey.300" : "grey.50",
                },
              }}
            >
              <FilterListIcon />
            </IconButton>
          </MDBox>

          <FiltersDrawer
            open={filtersOpen}
            statuses={filtering.statuses as JobStatus[]}
            onStatusChange={handleStatusChange}
            cities={selectedCities}
            onCitiesChange={handleCitiesChange}
            organizationProductTypeIds={organizationProductTypeIds}
            onProductTypeChange={handleProductTypeChange}
            sortBy={sortBy}
            onSortChange={handleSortChange}
          />
        </MDBox>

        <MDBox
          ml={2}
          mr={2}
          pt={2}
          pb={2}
          mb={2}
          sx={{
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: "8px",
            backgroundColor: "white!important",
          }}
        >
          {error ? (
            <MDBox p={2}>
              <MDTypography variant="body2" color="error">
                Error loading jobs
              </MDTypography>
            </MDBox>
          ) : loading && (!jobs || jobs.length === 0) ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={24} />
            </MDBox>
          ) : !jobs || jobs.length === 0 ? (
            <MDBox p={2} textAlign="center">
              <MDTypography variant="body2" color="text" mb={1}>
                No jobs found
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                Try adjusting your filters to see more results
              </MDTypography>
            </MDBox>
          ) : (
            jobs.map((job, index) => (
              <React.Fragment key={job.id}>
                <JobCard job={job} />
                {index < jobs.length - 1 && <Divider sx={{}} />}
              </React.Fragment>
            ))
          )}
        </MDBox>
      </MDBox>

      {pagination.paginatorInfo && pagination.total > 0 && (
        <MDBox p={2} borderTop="1px solid" borderColor="grey.200">
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="caption" color="text">
              Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
              {Math.min(pagination.currentPage * pagination.pageSize, pagination.total)} of{" "}
              {pagination.total} jobs
            </MDTypography>

            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={pagination.pageSize}
                onChange={(e) => pagination.setPageSize(Number(e.target.value))}
                sx={{ fontSize: "12px", height: "28px" }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </MDBox>

          <MDBox display="flex" justifyContent="center">
            <Pagination
              count={pagination.pages}
              page={pagination.currentPage}
              onChange={(_, page) => pagination.setCurrentPage(page)}
              size="small"
              shape="rounded"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  fontSize: "12px",
                  minWidth: "32px",
                  height: "32px",
                },
              }}
            />
          </MDBox>

          <MDBox mt={2} textAlign="center">
            <Link
              to="/jobs/schedule-classic"
              style={{
                fontSize: "0.75rem",
                color: "#1976d2",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
              }}
            >
              Go to old scheduling page <span style={{ fontSize: "1rem" }}>→</span>
            </Link>
          </MDBox>
        </MDBox>
      )}
    </Paper>
  );
}
